import { type RouteComponentProps } from "react-router-dom"
import * as t from "io-ts"
import { DateFromISOString } from "io-ts-types/lib/DateFromISOString"
import { NumberFromString } from "io-ts-types/lib/NumberFromString"
import { propertyAddressSchema } from "./address"
import * as timeline from "./postSubmissionTimeline"

import {
  type JourneyState,
  JourneyStateTag,
  ContractStep,
} from "@heyhabito/plus-client"
export {
  type PlusQuoteBreakdown,
  type SurveyType,
} from "@heyhabito/plus-client"

/**
 * Basic newtypes
 * ----------------------------------------------------------------------------
 */
export type ExAppId = string // NOTE: could be "new"

export const exAppIdSchema = t.string

export type Token = string

export const tokenSchema = t.string

export type Url = string

export const urlSchema = t.string

/**
 * Application journey states as understood by `engine` and other apps around
 * the Habito ecosystem
 * ----------------------------------------------------------------------------
 */
export enum AppJourneyState {
  InFactFind = "InFactFind",
  InPostFactFind = "InPostFactFind",
  InHumanAdvice = "InHumanAdvice",
  InDiscussion = "InDiscussion",
  InPostDiscussion = "InPostDiscussion",
  InAIP = "InAIP",
  InPostAIP = "InPostAIP",
  InSubmission = "InSubmission",
  InPostSubmission = "InPostSubmission",
  InUnderwriting = "InUnderwriting",
  InPostUnderwriting = "InPostUnderwriting",
  Declined = "Declined",
  InOffer = "InOffer",
  ApplicationCompleted = "ApplicationCompleted",
  ApplicationDeleted = "ApplicationDeleted",
}
export const appJourneyStates = Object.keys(AppJourneyState).map(
  k => k as AppJourneyState
)

// https://github.com/Microsoft/TypeScript/issues/17198#issuecomment-358666450
export const appJourneyStateSchema: t.Type<AppJourneyState> = t.keyof({
  [AppJourneyState.InFactFind]: null,
  [AppJourneyState.InPostFactFind]: null,
  [AppJourneyState.InHumanAdvice]: null,
  [AppJourneyState.InDiscussion]: null,
  [AppJourneyState.InPostDiscussion]: null,
  [AppJourneyState.InAIP]: null,
  [AppJourneyState.InPostAIP]: null,
  [AppJourneyState.InSubmission]: null,
  [AppJourneyState.InPostSubmission]: null,
  [AppJourneyState.InUnderwriting]: null,
  [AppJourneyState.InPostUnderwriting]: null,
  [AppJourneyState.Declined]: null,
  [AppJourneyState.InOffer]: null,
  [AppJourneyState.ApplicationCompleted]: null,
  [AppJourneyState.ApplicationDeleted]: null,
})

/**
 * Arbitrary stages that are only a concern of this web app
 * ----------------------------------------------------------------------------
 */
export enum FrontendStage {
  AboutMe = "AboutMe",
  ChatPreferences = "ChatPreferences",
  ExpertHelp = "ExpertHelp",
  YourRecommendation = "YourRecommendation",
  ApplicationSpace = "ApplicationSpace",
  TrackMyMortgage = "TrackMyMortgage",
  Deleted = "Deleted",
}

export const frontendJourneyStages = Object.keys(FrontendStage).map(
  k => k as FrontendStage
)

export const prettyPrintFrontendState = (stage: FrontendStage): string => {
  switch (stage) {
    case FrontendStage.AboutMe:
      return "Tell us about you"
    case FrontendStage.ChatPreferences:
      return "Chat preference"
    case FrontendStage.ExpertHelp:
      return "Chat to an expert"
    case FrontendStage.YourRecommendation:
      return "Get a recommendation"
    case FrontendStage.ApplicationSpace:
      return "Apply for a mortgage"
    case FrontendStage.TrackMyMortgage:
      return "Track your mortgage"
    case FrontendStage.Deleted:
      return "Deleted"
  }
}

/**
 * My Account endpoint
 * ----------------------------------------------------------------------------
 */
export const userDetailsSchema = t.strict({
  accountId: t.string,
  email: t.string,
  intercomUserJwt: t.string,
})

export type UserDetails = t.TypeOf<typeof userDetailsSchema>

export const mortgagePurposeSchema = t.strict({
  type: t.keyof({
    ToRemortgage: null,
    ToBuyAHouse: null,
  }),
})

export type MortgagePurpose = t.TypeOf<typeof mortgagePurposeSchema>

export const mortgageTypeSchema = t.strict({
  type: t.keyof({
    BuyToLet: null,
    Residential: null,
  }),
})

export type MortgageType = t.TypeOf<typeof mortgageTypeSchema>

export const purchaseReasonSchema = t.strict({
  type: t.keyof({
    MyFirstPurchase: null,
    MovingHouse: null,
    AnAdditionalProperty: null,
  }),
})

export type PurchaseReason = t.TypeOf<typeof purchaseReasonSchema>

export const completionCelebratedSchema = t.strict({
  celebrated: t.boolean,
})

export type CompletionCelebrated = t.TypeOf<typeof completionCelebratedSchema>

export const applicationSchema = t.strict({
  applicationCreatedTimestamp: t.string,
  applicationJourney: t.strict({
    type: appJourneyStateSchema,
  }),
  applicationType: t.union([
    t.null,
    t.strict({
      type: t.string,
    }),
  ]),
  childApplicationId: t.union([t.null, exAppIdSchema]),
  externalApplicationId: exAppIdSchema,
  fullName: t.union([
    t.null,
    t.strict({
      forename: t.string,
      surname: t.string,
    }),
  ]),
  mortgageAmount: t.union([t.null, t.string]),
  mortgagePurpose: t.union([t.null, mortgagePurposeSchema]),
  purchaseReason: t.union([t.null, purchaseReasonSchema]),
  propertyAddress: t.union([
    t.null,
    t.strict({
      value: propertyAddressSchema,
    }),
  ]),
  transactionScenario: t.union([t.null, mortgageTypeSchema]),
})

export type Application = t.TypeOf<typeof applicationSchema>

export const getFirstNameFor = (
  apps: Application[],
  externalAppId: string
): string | null => {
  for (const a of apps) {
    if (a.externalApplicationId == externalAppId) {
      return a.fullName && a.fullName.forename ? a.fullName.forename : null
    }
  }
  return null
}

export const getValidFirstNames = (apps: Application[]): string[] => {
  return apps.flatMap(a =>
    a.fullName && a.fullName.forename ? [a.fullName.forename] : []
  )
}

export const getFirstName = (
  apps: Application[],
  externalAppId?: string
): string | null => {
  if (externalAppId) {
    return getFirstNameFor(apps, externalAppId)
  } else {
    // Use the latest valid first name if no app is specified
    const by = (v: Application): number =>
      Number(new Date(v.applicationCreatedTimestamp))
    const sortedApps = [...apps].sort((a, b) => by(b) - by(a))
    const [firstName] = getValidFirstNames(sortedApps)
    return firstName || null
  }
}

export const myAccountSchema = t.strict({
  account: userDetailsSchema,
  applications: t.array(applicationSchema),
})

export type MyAccount = t.TypeOf<typeof myAccountSchema>

/**
 * FactFind availability endpoint
 * ----------------------------------------------------------------------------
 */

export const availabilitySchema = t.union([
  t.strict({ type: t.literal("Locked") }),
  t.strict({ type: t.literal("Nothing") }),
  t.strict({ type: t.literal("Unlocked") }),
])

export type Availability = t.TypeOf<typeof availabilitySchema>

export const documentsAvailabilitySchema = t.union([
  t.strict({ type: t.literal("DocumentsUnavailable") }),
  t.strict({ type: t.literal("DocumentsAvailable") }),
])

export type DocumentsAvailability = t.TypeOf<typeof documentsAvailabilitySchema>

export const factFindAvailabilitySchema = t.strict({
  completionAvailability: t.union([t.null, availabilitySchema]),
  documentsAvailability: t.union([t.null, documentsAvailabilitySchema]),
  plusJourneyAvailability: t.boolean,
})

export type FactFindAvailability = t.TypeOf<typeof factFindAvailabilitySchema>

/**
 * AsyncRecStatus endpoint
 * ----------------------------------------------------------------------------
 */

export type AsyncRecStatus = {
  asyncRecEnabledInHours: boolean
  asyncRecEnabledOutOfHours: boolean
}

/**
 * ManualAdviceMethod endpoint
 * ----------------------------------------------------------------------------
 */

export const manualAdviceMethodSchema = t.union([
  t.strict({ type: t.literal("ChatStarted") }),
  t.strict({ type: t.literal("ChatBooked") }), // deprecated option
  t.strict({ type: t.literal("ChatReminder") }), // deprecated option
  t.strict({ type: t.literal("ChatByEmail"), value: t.string }),
])

export type ManualAdviceMethod = t.TypeOf<typeof manualAdviceMethodSchema>

export const ChatStarted: ManualAdviceMethod = { type: "ChatStarted" }
export const ChatBooked: ManualAdviceMethod = { type: "ChatBooked" }
export const ChatReminder: ManualAdviceMethod = { type: "ChatReminder" }

export function chatStarted(choices: ManualAdviceChoices[]): ChatPreference {
  return {
    method: ChatStarted,
    choices: choices,
  }
}

export function chatByEmail(
  message: string,
  choices: ManualAdviceChoices[]
): ChatPreference {
  const method: ManualAdviceMethod = {
    type: "ChatByEmail",
    value: message,
  }

  return {
    method: method,
    choices: choices,
  }
}

export type ManualAdviceChoices =
  | { type: "ChatStartedChoice" }
  | { type: "ChatByEmailChoice" }

export const ChatStartedChoice: ManualAdviceChoices = {
  type: "ChatStartedChoice",
}
export const ChatByEmailChoice: ManualAdviceChoices = {
  type: "ChatByEmailChoice",
}

export type ChatPreference = {
  method: ManualAdviceMethod
  choices: ManualAdviceChoices[]
}

/**
 * Self-serve MIP journey endpoint
 * ----------------------------------------------------------------------------
 */

export type mipId = string // uuid

export const mipIdSchema = t.string

export const mipSchema = t.strict({
  mipId: mipIdSchema,
  amount: t.number,
  submitted: t.string, // timestamp
  propertyBudget: t.number,
  exAppId: t.union([t.null, t.string]),
})

export type Mip = t.TypeOf<typeof mipSchema>

export type journeyId = string // uuid

export const journeyIdSchema = t.string

export const mipJourneySchema = t.strict({
  journeyId: journeyIdSchema,
  mips: t.array(mipSchema),
})

export type MipJourney = t.TypeOf<typeof mipJourneySchema>

/**
 * Mortgage details overview endpoint
 * ----------------------------------------------------------------------------
 */

export type LenderId = string

export const lenderIdSchema = t.string

export type LenderName = string

export const lenderNameSchema = t.string

export const lenderSchema = t.strict({
  id: t.union([t.null, lenderIdSchema]),
  name: t.union([t.null, lenderNameSchema]),
})

export type Lender = t.TypeOf<typeof lenderSchema>

export const initialPeriodEndConditionSchema = t.union([
  t.strict({
    type: t.literal("EndOn"),
    value: t.union([t.null, DateFromISOString]),
  }),
  t.strict({
    type: t.literal("EndAfter"),
    value: t.union([t.null, t.number]),
  }),
])

export type LoanToValue = number

export const loanToValueSchema = NumberFromString

export type CaseId = string

export const caseIdSchema = t.string

export type ProductName = string

export const productNameSchema = t.string

export type ProductId = string

export const productIdSchema = t.string

export const productTypeSchema = t.union([
  t.strict({ type: t.literal("FixedProduct") }),
  t.strict({ type: t.literal("VariableProduct") }),
])

export type ProductType = t.TypeOf<typeof productTypeSchema>

export type InitialRate = number

export const initialRateSchema = NumberFromString

export type PropertyValue = number

export const propertyValueSchema = NumberFromString

export type FinalMortgageAmount = number

export const finalMortgageAmountSchema = NumberFromString

export type MortgageTerm = number

export const mortgageTermSchema = t.number

export type ProductExpiryDate = string

export const productExpiryDateSchema = t.string

export type MonthlyRepayments = number

export const monthlyRepaymentsSchema = NumberFromString

export type StandardVariableRate = number

export const standardVariableRateSchema = NumberFromString

export type CompletionDate = string

export const completionDateSchema = t.string

export const mortgageDetailsSchema = t.strict({
  lender: t.union([t.null, lenderSchema]),
  loanToValue: t.union([t.null, loanToValueSchema]),
  caseId: t.union([t.null, caseIdSchema]),
  productName: t.union([t.null, productNameSchema]),
  productId: t.union([t.null, productIdSchema]),
  productType: t.union([t.null, productTypeSchema]),
  mortgagePurpose: t.union([t.null, mortgagePurposeSchema]),
  mortgageType: t.union([t.null, mortgageTypeSchema]),
  initialRate: t.union([t.null, initialRateSchema]),
  propertyValue: t.union([t.null, propertyValueSchema]),
  finalMortgageAmount: t.union([t.null, finalMortgageAmountSchema]),
  mortgageTerm: t.union([t.null, mortgageTermSchema]),
  productExpiryDate: t.union([t.null, productExpiryDateSchema]),
  monthlyRepayments: t.union([t.null, monthlyRepaymentsSchema]),
  standardVariableRate: t.union([t.null, standardVariableRateSchema]),
  completionDate: t.union([t.null, completionDateSchema]),
  initialPeriodEndCondition: t.union([t.null, initialPeriodEndConditionSchema]),
})

export type MortgageDetails = t.TypeOf<typeof mortgageDetailsSchema>

/**
 * Mortgage tracker api endpoint
 * ----------------------------------------------------------------------------
 */

export const mortgageTrackerJourneySummarySchema = t.strict({
  journeyId: t.string,
  associatedApplicationJourney: t.union([t.null, t.string]),
  details: t.union([
    t.null,
    t.strict({
      currentMortgage: t.strict({
        svrDate: t.string,
      }),
    }),
  ]),
})

export type MortgageTrackerJourneySummary = t.TypeOf<
  typeof mortgageTrackerJourneySummarySchema
>

/**
 * Documents upload status endpoint
 * ----------------------------------------------------------------------------
 */

const documentsUploadStatusLiterals = t.keyof({
  AlreadyNotified: null,
  CannotNotify: null,
  Notifiable: null,
  ShowBannerNotifiable: null,
})

export const documentsUploadStatus = t.strict({
  type: documentsUploadStatusLiterals,
})

export type DocumentsUploadStatus = t.TypeOf<typeof documentsUploadStatus>

/**
 * Fact Find completion overview endpoint
 * ----------------------------------------------------------------------------
 */

export const partialFactFindCompletionOverview = t.strict({
  groups: t.array(
    t.strict({
      contents: t.array(
        t.strict({
          status: t.array(t.strict({ type: t.string })),
        })
      ),
    })
  ),
})

type PartialFactFindCompletionOverview = t.TypeOf<
  typeof partialFactFindCompletionOverview
>

export type FactFindCompletionStatuses =
  PartialFactFindCompletionOverview["groups"][number]["contents"]

/**
 * Plus contract state endpoint
 * ----------------------------------------------------------------------------
 */

const plusPartialJourneyState: t.Type<JourneyState> = t.union([
  t.strict({
    tag: t.literal(JourneyStateTag.ReviewingContract),
    contractStep: t.keyof({
      [ContractStep.FeeStep]: null,
      [ContractStep.SurveyorStep]: null,
      [ContractStep.DataStep]: null,
      [ContractStep.WaiverStep]: null,
      [ContractStep.ProtectionsStep]: null,
      [ContractStep.GoWrongStep]: null,
      [ContractStep.WhatIsHabitoPlusStep]: null,
      [ContractStep.WhatFeesStep]: null,
      [ContractStep.CollectingDataStep]: null,
      [ContractStep.DisclaimerStep]: null,
      [ContractStep.CommunicationsStep]: null,
      [ContractStep.ComplaintsStep]: null,
      [ContractStep.GeneralStep]: null,
    }),
    paymentTimestamp: t.union([t.null, t.string]),
  }),
  t.strict({
    tag: t.literal(JourneyStateTag.SigningContract),
    paymentTimestamp: t.union([t.null, t.string]),
  }),
  t.strict({ tag: t.literal(JourneyStateTag.Paying) }),
  t.strict({
    tag: t.literal(JourneyStateTag.Done),
    paymentTimestamp: t.string,
  }),
])

export const plusPartialContractState = t.strict({
  journeyState: plusPartialJourneyState,
  surveyType: t.keyof({
    None: null,
    HomebuyersReport: null,
    HomebuyersReportWithValuation: null,
    BuildingSurvey: null,
  }),
})

export type PlusPartialContractState = t.TypeOf<typeof plusPartialContractState>

/**
 * Post submission timeline endpoint
 * ----------------------------------------------------------------------------
 */

export const postSubmissionTimelineSchema =
  timeline.postSubmissionTimelineSchema
export const documentChecksAndValuationMilestoneSchema =
  timeline.documentChecksAndValuationMilestoneSchema
export const plusMilestonesSchema = timeline.plusMilestonesSchema
export const homebuyerSurveyMilestoneSchema =
  timeline.homebuyerSurveyMilestoneSchema
export const legalWorkMilestoneSchema = timeline.legalWorkMilestoneSchema
export const mortgageOfferMilestoneSchema =
  timeline.mortgageOfferMilestoneSchema
export const submissionMilestoneSchema = timeline.submissionMilestoneSchema
export const mortgageCompletionMilestoneSchema =
  timeline.mortgageCompletionMilestoneSchema

export type PostSubmissionTimeline = t.TypeOf<
  typeof timeline.postSubmissionTimelineSchema
>
export type SubmissionMilestone = t.TypeOf<
  typeof timeline.submissionMilestoneSchema
>
export type DocumentChecksAndValuationMilestone = t.TypeOf<
  typeof timeline.documentChecksAndValuationMilestoneSchema
>
export type MortgageOfferMilestone = t.TypeOf<
  typeof timeline.mortgageOfferMilestoneSchema
>
export type PlusMilestones = t.TypeOf<typeof timeline.plusMilestonesSchema>
export type SurveyMilestone = t.TypeOf<
  typeof timeline.homebuyerSurveyMilestoneSchema
>
export type LegalWorkMilestone = t.TypeOf<
  typeof timeline.legalWorkMilestoneSchema
>
export type CompleteMortgageMilestone = t.TypeOf<
  typeof timeline.mortgageCompletionMilestoneSchema
>

export type MilestoneStatus = t.TypeOf<typeof timeline.milestoneStatus>
export type StepStatus = t.TypeOf<typeof timeline.stepStatus>
export type Step = timeline.Step
export type MilestoneStatusType = timeline.MilestoneStatusType
export type StepStatusType = timeline.StepStatusType
export type Solicitor = t.TypeOf<typeof timeline.solicitorSchema>
export type SolicitorType = timeline.SolicitorType

export const getHomebuyerSurveyMilestoneSteps =
  timeline.getHomebuyerSurveyMilestoneSteps
export const getLegalWorkMilestoneSteps = timeline.getLegalWorkMilestoneSteps
export const getDocumentChecksAndValuationMilestoneSteps =
  timeline.getDocumentChecksAndValuationMilestoneSteps
export const getMortgageOfferMilestoneSteps =
  timeline.getMortgageOfferMilestoneSteps

/**
 * Helper globals
 * ----------------------------------------------------------------------------
 */

export interface RouteParams {
  exAppId: ExAppId
}

export type RouteProps = RouteComponentProps<RouteParams>
